import { useMutation, useQuery } from "convex/react";
import { SparklesIcon, WorkflowIcon } from "lucide-react";
import type { KeyboardEventHandler } from "react";
import { useCallback, useMemo, useRef, useState } from "react";
import { usePromptInputController } from "@/components/ai-elements/prompt-input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatRelativeTime } from "@/lib/time";
import { cn } from "@/lib/utils";
import {
	findActiveMention,
	insertMention,
} from "../../../../email-assistant/core/workflowMentions";
import { api } from "../../../convex/_generated/api";

export type ReferenceKind = "workflow" | "skill";

export type MentionResource = {
	id: string;
	name: string;
	description: string;
};

type ReferenceMentionMenuProps = {
	kind: ReferenceKind;
	items: readonly MentionResource[];
	selectedIndex: number;
	lastCheckedAt?: number | null;
	isRefreshDisabled?: boolean;
	onSelect: (item: MentionResource) => void;
	onHighlight: (index: number) => void;
	onRefresh?: () => void;
};

export function ReferenceMentionMenu({
	kind,
	items,
	selectedIndex,
	lastCheckedAt,
	isRefreshDisabled = false,
	onSelect,
	onHighlight,
	onRefresh,
}: ReferenceMentionMenuProps) {
	const selectedItemRef = useCallback((node: HTMLButtonElement | null) => {
		node?.scrollIntoView({ block: "nearest" });
	}, []);
	const isWorkflow = kind === "workflow";
	const Icon = isWorkflow ? WorkflowIcon : SparklesIcon;
	const title = isWorkflow ? "Workflows" : "Skills";
	const source = isWorkflow
		? "Clerical Workflows · OneDrive"
		: "Clerical-managed capabilities";
	const empty = isWorkflow
		? "No workflows found — add supported SOPs to your Clerical Workflows folder"
		: "No active skills available";
	const syncLabel = lastCheckedAt
		? `Synced ${formatRelativeTime(lastCheckedAt)}`
		: "Not synced";

	return (
		<div
			data-testid="reference-mention-menu"
			data-reference-kind={kind}
			role="listbox"
			aria-label={title}
			className="absolute right-0 bottom-full left-0 z-50 mb-2 animate-in rounded-lg border bg-popover p-1 text-popover-foreground shadow-md fade-in-0 slide-in-from-bottom-1 duration-150"
		>
			<div className="flex items-center justify-between gap-3 border-b px-2 py-1.5">
				<div className="flex items-center gap-1.5 text-xs font-semibold">
					<Icon className="size-3.5 text-primary" />
					<span>{title}</span>
				</div>
				<span className="truncate text-[10px] text-muted-foreground">
					{source}
				</span>
			</div>
			<ScrollArea className="max-h-64">
				<div className="flex flex-col gap-0.5 py-1">
					{items.length === 0 ? (
						<div className="px-2 py-2 text-xs text-muted-foreground">
							{empty}
						</div>
					) : (
						items.map((item, index) => (
							<button
								key={item.id}
								ref={index === selectedIndex ? selectedItemRef : undefined}
								type="button"
								data-testid={`${kind}-mention-item`}
								role="option"
								aria-selected={index === selectedIndex}
								className={cn(
									"flex w-full min-w-0 flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left transition-colors",
									index === selectedIndex
										? "bg-primary/10 text-foreground"
										: "hover:bg-muted",
								)}
								onMouseEnter={() => onHighlight(index)}
								onMouseDown={(event) => {
									event.preventDefault();
									onSelect(item);
								}}
							>
								<span className="w-full truncate text-xs font-medium">
									{item.name}
								</span>
								<span className="w-full truncate text-[11px] text-muted-foreground">
									{item.description}
								</span>
							</button>
						))
					)}
				</div>
			</ScrollArea>
			{isWorkflow && onRefresh ? (
				<div className="flex items-center gap-1 border-t px-2 py-1.5 text-[10px] text-muted-foreground">
					<span>{syncLabel}</span>
					<span aria-hidden="true">·</span>
					<button
						type="button"
						disabled={isRefreshDisabled}
						className={cn(
							"font-medium text-primary hover:underline",
							"disabled:pointer-events-none disabled:text-muted-foreground disabled:no-underline",
						)}
						onClick={onRefresh}
					>
						Refresh
					</button>
				</div>
			) : null}
		</div>
	);
}

export function useResourceMentions() {
	const controller = usePromptInputController();
	const workflows = useQuery(
		api.agent.workflows.workflowIndexQueries.listWorkflows,
		{},
	);
	const skills = useQuery(api.agent.skills.library.listSkillsForOperators, {});
	const workflowIndexStatus = useQuery(
		api.agent.workflows.workflowIndexQueries.getWorkflowIndexStatus,
		{},
	);
	const requestWorkflowIndexRefresh = useMutation(
		api.agent.workflows.workflowIndexQueries.requestWorkflowIndexRefresh,
	);
	const activeMention = findActiveMention(controller.textInput.value);
	const mentionKey = activeMention
		? `${activeMention.start}:${activeMention.trigger}:${activeMention.query}`
		: null;
	const kind: ReferenceKind | null =
		activeMention?.trigger === "@"
			? "workflow"
			: activeMention?.trigger === "/"
				? "skill"
				: null;
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [isRefreshDisabled, setIsRefreshDisabled] = useState(false);
	const [dismissedMentionKey, setDismissedMentionKey] = useState<string | null>(
		null,
	);
	const resources = useMemo<MentionResource[] | undefined>(() => {
		if (kind === "workflow") {
			return workflows?.map((workflow) => ({
				id: `${workflow.driveId}:${workflow.fileId}`,
				name: workflow.name,
				description: workflow.description,
			}));
		}
		if (kind === "skill") {
			return skills?.map((skill) => ({
				id: skill.slug,
				name: skill.name,
				description: skill.description,
			}));
		}
		return undefined;
	}, [kind, skills, workflows]);
	const filteredResources = useMemo(() => {
		if (!activeMention || !resources) return [];
		const query = activeMention.query.toLocaleLowerCase();
		return resources.filter(
			(resource) =>
				resource.name.toLocaleLowerCase().includes(query) ||
				resource.description.toLocaleLowerCase().includes(query),
		);
	}, [activeMention, resources]);

	const lastMentionKeyRef = useRef<string | null>(mentionKey);
	if (lastMentionKeyRef.current !== mentionKey) {
		lastMentionKeyRef.current = mentionKey;
		if (selectedIndex !== 0) setSelectedIndex(0);
		if (dismissedMentionKey !== null) setDismissedMentionKey(null);
	}

	const isOpen =
		activeMention !== null &&
		kind !== null &&
		resources !== undefined &&
		(filteredResources.length > 0 || resources.length === 0) &&
		dismissedMentionKey !== mentionKey;
	const activeIndex =
		filteredResources.length > 0 ? selectedIndex % filteredResources.length : 0;
	const selectResource = useCallback(
		(resource: MentionResource) => {
			if (!activeMention) return;
			controller.textInput.setInput(
				insertMention(controller.textInput.value, activeMention, resource.name),
			);
		},
		[activeMention, controller.textInput],
	);
	const refreshWorkflowIndex = useCallback(() => {
		if (isRefreshDisabled) return;
		setIsRefreshDisabled(true);
		void requestWorkflowIndexRefresh({}).catch(() => undefined);
		window.setTimeout(() => setIsRefreshDisabled(false), 2_000);
	}, [isRefreshDisabled, requestWorkflowIndexRefresh]);
	const onKeyDown: KeyboardEventHandler<HTMLTextAreaElement> = useCallback(
		(event) => {
			if (event.nativeEvent.isComposing || !isOpen) return;
			if (filteredResources.length === 0) {
				if (event.key === "Escape") {
					event.preventDefault();
					setDismissedMentionKey(mentionKey);
				}
				return;
			}
			if (event.key === "ArrowDown" || event.key === "ArrowUp") {
				event.preventDefault();
				const direction = event.key === "ArrowDown" ? 1 : -1;
				setSelectedIndex(
					(current) =>
						(current + direction + filteredResources.length) %
						filteredResources.length,
				);
				return;
			}
			if (event.key === "Enter" || event.key === "Tab") {
				event.preventDefault();
				const resource = filteredResources[activeIndex];
				if (resource) selectResource(resource);
				return;
			}
			if (event.key === "Escape") {
				event.preventDefault();
				setDismissedMentionKey(mentionKey);
			}
		},
		[activeIndex, filteredResources, isOpen, mentionKey, selectResource],
	);

	return {
		menuEl:
			isOpen && kind ? (
				<ReferenceMentionMenu
					kind={kind}
					items={filteredResources}
					selectedIndex={activeIndex}
					lastCheckedAt={workflowIndexStatus?.lastCheckedAt}
					isRefreshDisabled={isRefreshDisabled}
					onSelect={selectResource}
					onHighlight={setSelectedIndex}
					onRefresh={kind === "workflow" ? refreshWorkflowIndex : undefined}
				/>
			) : null,
		onKeyDown,
	};
}
